/**
 * Background Sync Service
 * Handles offline form submissions and background synchronization
 */

const SYNC_QUEUE_KEY = 'plutoo_form_sync_queue';
const MAX_QUEUE_SIZE = 50;

class BackgroundSyncService {
  constructor() {
    this.isOnline = navigator.onLine;
    this.setupEventListeners();
  }

  setupEventListeners() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  /**
   * Add a form submission to the sync queue
   * @param {Object} submission - The form data to sync
   * @param {string} submission.type - Type of submission (e.g., 'maintenance', 'tenant', etc.)
   * @param {Object} submission.data - The actual form data
   * @param {string} submission.endpoint - API endpoint to submit to
   * @param {Object} submission.options - Additional options (method, headers, etc.)
   */
  async addToQueue(submission) {
    try {
      const queue = this.getQueue();
      const syncItem = {
        id: this.generateId(),
        timestamp: new Date().toISOString(),
        ...submission,
        retryCount: 0,
        maxRetries: 3
      };

      queue.push(syncItem);
      this.saveQueue(queue);

      console.log('📋 Added to sync queue:', syncItem.type, syncItem.id);

      // If back online, process immediately
      if (this.isOnline) {
        await this.processQueue();
      }

      return syncItem.id;
    } catch (error) {
      console.error('❌ Failed to add to sync queue:', error);
      throw error;
    }
  }

  /**
   * Process the sync queue
   */
  async processQueue() {
    if (!this.isOnline) {
      console.log('⏸️ Skipping sync - offline');
      return;
    }

    const queue = this.getQueue();
    if (queue.length === 0) {
      console.log('📋 Sync queue empty');
      return;
    }

    console.log(`🔄 Processing ${queue.length} queued submissions`);

    const results = [];

    for (let i = queue.length - 1; i >= 0; i--) {
      const item = queue[i];

      try {
        await this.submitItem(item);
        queue.splice(i, 1); // Remove successful item
        results.push({ id: item.id, success: true });
        console.log(`✅ Synced: ${item.type} (${item.id})`);
      } catch (error) {
        console.error(`❌ Failed to sync: ${item.type} (${item.id})`, error);

        item.retryCount++;

        if (item.retryCount >= item.maxRetries) {
          queue.splice(i, 1); // Remove failed item after max retries
          results.push({ id: item.id, success: false, error: 'Max retries exceeded' });
          console.log(`🗑️ Removed failed item after ${item.maxRetries} retries`);
        } else {
          results.push({ id: item.id, success: false, error: error.message });
          console.log(`⏳ Will retry ${item.type} (${item.id}) - attempt ${item.retryCount}/${item.maxRetries}`);
        }
      }
    }

    // Save updated queue
    this.saveQueue(queue);

    // Register background sync if supported
    if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
      await this.registerBackgroundSync();
    }

    return results;
  }

  /**
   * Submit a single item from the queue
   */
  async submitItem(item) {
    const { endpoint, data, options = {} } = item;
    const method = options.method || 'POST';
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    const response = await fetch(endpoint, {
      method,
      headers,
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get the current sync queue
   */
  getQueue() {
    try {
      const stored = localStorage.getItem(SYNC_QUEUE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('❌ Error reading sync queue:', error);
      return [];
    }
  }

  /**
   * Save the sync queue
   */
  saveQueue(queue) {
    try {
      // Limit queue size
      if (queue.length > MAX_QUEUE_SIZE) {
        queue = queue.slice(-MAX_QUEUE_SIZE);
        console.log(`📋 Queue trimmed to ${MAX_QUEUE_SIZE} items`);
      }

      localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
    } catch (error) {
      console.error('❌ Error saving sync queue:', error);
    }
  }

  /**
   * Clear the sync queue
   */
  clearQueue() {
    localStorage.removeItem(SYNC_QUEUE_KEY);
    console.log('🧹 Sync queue cleared');
  }

  /**
   * Get queue statistics
   */
  getQueueStats() {
    const queue = this.getQueue();
    return {
      total: queue.length,
      byType: queue.reduce((acc, item) => {
        acc[item.type] = (acc[item.type] || 0) + 1;
        return acc;
      }, {}),
      oldest: queue.length > 0 ? queue[0].timestamp : null,
      newest: queue.length > 0 ? queue[queue.length - 1].timestamp : null
    };
  }

  /**
   * Generate a unique ID for sync items
   */
  generateId() {
    return `sync_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Register background sync with service worker
   */
  async registerBackgroundSync() {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register('background-sync-forms');
      console.log('📡 Background sync registered');
    } catch (error) {
      console.error('❌ Background sync registration failed:', error);
    }
  }

  /**
   * Check if background sync is supported
   */
  isBackgroundSyncSupported() {
    return 'serviceWorker' in navigator &&
           'sync' in window.ServiceWorkerRegistration.prototype;
  }
}

// Create singleton instance
const backgroundSyncService = new BackgroundSyncService();

// Export for use in components
export default backgroundSyncService;

// Export class for testing
export { BackgroundSyncService };

// Helper function for easy form submission with offline support
export const submitFormWithSync = async (formData, endpoint, options = {}) => {
  const submission = {
    type: 'form_submission',
    data: formData,
    endpoint,
    options
  };

  return backgroundSyncService.addToQueue(submission);
};
